import { Box, Text, Switch, HStack } from '@chakra-ui/react';
import LineItemGroup from './LineItemGroup';
import { useContext } from 'react';
import useCurrencyFormatter from '../hooks/useCurrencyFormatter';
import StepperContext from '../contexts/StepperContext';
import InvoicesToPayContext from '../contexts/InvoicesToPayContext';
import SubscriptionsContext from '../contexts/SubscriptionsContext';
import InvoiceDataContext from '../contexts/InvoiceDataContext';
import SubscriptionManagementContext from '../contexts/SubscriptionManagementContext';
import TabContext from '../contexts/TabContext';
import { useBreakpointsContext } from '../hooks/useBreakpointsContext';
import { INVOICE_TABS } from '../constants/enums';
import { calculatePaymentTotals } from '../helpers/credits';

type Props = {
  compact?: boolean;
};

const PaymentSummaryTotalAmount = ({ compact }: Props): JSX.Element => {
  const formatCurrency = useCurrencyFormatter();
  const { paymentMethodContext, resetStepper } = useContext(StepperContext);
  const { totalAmountToPay: invoiceTotal } = useContext(InvoicesToPayContext);
  const { totalSubscriptionAmount } = useContext(SubscriptionsContext);
  const { paymentMethod } = paymentMethodContext;
  const { cardFeePercentage, creditsBalance, useCredits, setUseCredits } = useContext(InvoiceDataContext);
  const { setTabSelected } = useContext(TabContext);
  const { canManageSubscription, openManager } = useContext(SubscriptionManagementContext);
  const { isSmallScreen } = useBreakpointsContext();
  const { hasActiveSubscription, experiments } = useContext(SubscriptionsContext);

  const isCard = paymentMethod?.id === 'card';
  const { baseTotal, creditsApplied, cardFee, finalTotal } = calculatePaymentTotals({
    invoiceTotal,
    subscriptionTotal: totalSubscriptionAmount,
    creditsBalance,
    useCredits,
    cardFeePercentage: cardFeePercentage ?? 0,
    isCard,
  });

  const showCreditToggle = creditsBalance > 0 && baseTotal >= 3000;
  const adjustedCredits = creditsApplied < creditsBalance && creditsApplied < baseTotal;
  const unusedCredits = creditsBalance - creditsApplied;

  if (compact) {
    const items = [
      { label: 'Facturas', amount: invoiceTotal },
      ...(totalSubscriptionAmount > 0
        ? [
            {
                label: 'Palomma Plus',
                amount: totalSubscriptionAmount,
                editAction:
                  hasActiveSubscription && experiments?.isInPencilExperiment
                    ? canManageSubscription
                      ? openManager
                      : () => {
                          setTabSelected(INVOICE_TABS.PALOMMA_PLUS);
                          if (isSmallScreen) resetStepper(-1);
                        }
                    : undefined,
              },
            ]
        : []),
      ...(useCredits && creditsApplied > 0 ? [{ label: 'Saldo Palomma', amount: -creditsApplied }] : []),
      ...(isCard && cardFeePercentage ? [{ label: `+${cardFeePercentage}% tarjeta de crédito`, amount: cardFee }] : []),
    ];
    return (
      <Box py={3}>
        <LineItemGroup label="Total a pagar" amount={finalTotal} items={items} variant="total" />
      </Box>
    );
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gap={2} pt={4}>
      {showCreditToggle && (
        <HStack justifyContent="space-between" py={2}>
          <Box>
            <Text fontWeight={600} fontSize="sm">
              Usar Saldo Palomma
            </Text>
            <Text fontSize="xs" color="gray.500">
              Tienes {formatCurrency(creditsBalance)} disponibles
            </Text>
          </Box>
          <Switch isChecked={useCredits} onChange={(e) => setUseCredits(e.target.checked)} colorScheme="blue" />
        </HStack>
      )}
      {useCredits && creditsApplied > 0 && adjustedCredits && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Text fontSize="xs" color="gray.500">
            Aplicamos {formatCurrency(creditsApplied)} para mantener el pago mínimo de $3.000 COP.
          </Text>
          {unusedCredits > 0 && (
            <Text fontSize="xs" color="gray.500">
              Tus {formatCurrency(unusedCredits)} restantes quedan guardados para otro pago.
            </Text>
          )}
        </Box>
      )}
      <Box
        display={'flex'}
        flexDirection={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        borderTop={'2px solid'}
        borderColor={'#DFDEE2'}
        pt={3}
        mt={1}
      >
        <Text fontWeight={700} fontSize={'1rem'}>
          Total a pagar
        </Text>
        <Text fontWeight={700} fontSize={'1.25rem'}>
          {formatCurrency(finalTotal)}
        </Text>
      </Box>
    </Box>
  );
};

export default PaymentSummaryTotalAmount;
